"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import Link from "next/link";
import Image from '@/components/ui/Image';
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";

interface Hero3Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string;
    backgroundImage?: string;
    backgroundImageAltText?: string;
    overlayImage1?: string;
    overlayImage1AltText?: string;
    overlayImage2?: string;
    overlayImage2AltText?: string;
    overlayImage3?: string;
    overlayImage3AltText?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;  
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero3 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero3Props) => {
  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero3"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 3"
    > 
      <div className="container">
        <div className="grid grid-cols-1 items-center justify-center gap-12 lg:grid-cols-[minmax(33.75rem,_1.2fr)_1.5fr] lg:gap-8">
          <div>
            <div className="flex flex-col gap-6 text-center lg:text-left items-center lg:items-start">
              {contents?.badge && (
                <Badge variant="outline" bold>
                  {contents?.badge}
                </Badge>
              )}
              {contents?.title && (
                <h1>
                  {parse(contents.title)}
                </h1>
              )}
              {contents?.content && (
                <div className="mt-4 paragraph-lg">
                  {parse(contents?.content)}
                </div>
              )}
              {contents?.hasButton && (
                <div className="mt-2 w-full md:w-auto">
                  <div className="flex flex-col items-center gap-4 md:flex-row">
                    <Button
                      asChild
                      size='lg'
                      className="w-full md:w-auto"
                      variant={contents?.buttonStyle || "primary"}
                    >
                      <Link href={contents?.buttonLink ?? ""}>{contents?.buttonText}</Link>
                    </Button>
                    {contents?.secondaryButton && (
                      <Button
                        className="w-full md:w-auto"
                        variant={contents?.secondaryButtonStyle || "outline"}
                        asChild
                        size='lg'
                      >
                        <Link href={contents?.secondaryButtonLink ?? ""}> 
                          {contents?.secondaryButtonText}
                        </Link>
                      </Button>
                    )}
                  </div>
                  {contents?.microCopy && (
                    <p className="mt-2 paragraph-sm text-muted-foreground">
                      {contents?.microCopy}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="relative mr-auto ml-auto aspect-[1.28581291_/_1] w-full max-w-[37.25rem] lg:mr-0 lg:ml-auto">
              <div className="relative mx-auto aspect-[1.020365896_/_1] h-full w-[79.35%] max-w-[29.5625rem] overflow-hidden rounded-3xl">
                {contents?.backgroundImage && ( 
                  <Image
                    src={contents?.backgroundImage}
                    alt={contents?.backgroundImageAltText ?? ""}
                    title={contents?.backgroundImageAltText ?? ""}
                    className="relative z-10 w-full object-cover"
                    fill
                    priority
                    fetchPriority="high"
                  />
                )}
              </div>
              {contents?.overlayImage1 && (
                <div className="absolute top-[19.84%] -left-[-2%] z-30 aspect-[1.765043789_/_1] w-[30.49%] max-w-[11.875rem] overflow-hidden rounded-lg shadow-lg">
                  <Image
                    src={contents?.overlayImage1}
                    alt={contents?.overlayImage1AltText ?? ""}
                    title={contents?.overlayImage1AltText ?? ""}
                    className="size-full object-cover"
                    fill
                    priority
                    fetchPriority="high"
                  />
                </div>
              )}
              {contents?.overlayImage2 && (
                <div className="absolute top-[55%] left-[0%] z-30 aspect-[1.776555024_/_1] w-[43.6%] max-w-[16.375rem] overflow-hidden rounded-lg shadow-lg">
                  <Image
                    src={contents?.overlayImage2}
                    alt={contents?.overlayImage2AltText ?? ""}
                    title={contents?.overlayImage2AltText ?? ""}
                    className="size-full object-cover"
                    fill
                    priority
                    fetchPriority="high"
                  />
                </div>
              )}
              {contents?.overlayImage3 && (
                <div className="absolute top-[40%] right-[0%] z-30 aspect-[1.170212766_/_1] w-[26.48%] max-w-[10.3125rem] overflow-hidden rounded-lg shadow-lg">
                  <Image
                    src={contents?.overlayImage3}
                    alt={contents?.overlayImage3AltText ?? ""}
                    title={contents?.overlayImage3AltText ?? ""}
                    className="size-full object-cover"
                    fill
                    priority
                    fetchPriority="high"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Hero3 };



